import { createSlice } from "@reduxjs/toolkit";

const chat = createSlice({
  name: "socketConnection",
  initialState: {
    isConnected: false,
    socket: null,
    chatRooms: {
      pagination: {},
      results: [],
    },
    selectedRoom: {
      messages: {
        pagination: {},
        results: [],
      },
    },
    chatFilteredRooms: [],
  },
  reducers: {
    connectChatSocket: (state, action) => {
      state.isConnected = true;
      state.socket = action.payload;
    },
    disConnectChatSocket: (state, action) => {
      state.isConnected = false;
      state.socket = null;
    },
    fetchChatRooms: (state, action) => {
      state.chatRooms = action.payload;
    },
    loadMoreChatRooms: (state, action) => {
      state.chatRooms.results = [
        ...state.chatRooms.results,
        ...action.payload.results,
      ];
      state.chatRooms.pagination = action.payload.pagination;
    },
    selectRoom: (state, action) => {
      state.selectedRoom = action.payload;
    },
    appendNewMessage: (state, action) => {
      // Append new message to the selected room
      if (
        action.payload.roomInfo.channel_id === state.selectedRoom.channel_id
      ) {
        // Update messages list
        if (state.selectedRoom.messages?.results?.length > 0) {
          state.selectedRoom.messages.results = [
            action.payload.message,
            ...state.selectedRoom.messages.results,
          ];
        } else {
          state.selectedRoom.messages.results = [action.payload.message];
        }

        // Update room info
        state.selectedRoom.last_activity = action.payload.message.created_at;
        state.selectedRoom.last_msg_timesince =
          action.payload.roomInfo.msg_timesince;
        state.selectedRoom.last_msg_content =
          action.payload.roomInfo.msg_content_title;
        state.selectedRoom.last_msg_send_by = action.payload.message.send_by;
      }

      // Append new message to the chat rooms
      let rooms = state.chatRooms.results;

      // When room already exists in the list
      rooms = rooms.map((room) => {
        if (room.channel_id === action.payload.roomInfo.channel_id) {
          // Append new message to the messages list
          if (room.messages?.results?.length > 0) {
            // If already have messages, append new message to the end of the list
            room.messages.results = [
              action.payload.message,
              ...room.messages.results,
            ];
          } else {
            // If no messages, create new messages list
            room.messages = {
              results: [action.payload.message],
            };
          }

          // Update room info
          room.last_activity = action.payload.message.created_at;
          room.last_msg_timesince = action.payload.roomInfo.msg_timesince;
          room.last_msg_content = action.payload.roomInfo.msg_content_title;
          room.last_msg_send_by = action.payload.message.send_by;

          return room;
        }
        return room;
      });

      // sort rooms by last_activity
      rooms.sort((a, b) => {
        return new Date(b.last_activity) - new Date(a.last_activity);
      });

      state.chatRooms.results = rooms;
    },
    clearUnreadMessageCount: (state, action) => {
      state.chatRooms.results = state.chatRooms.results.map((room) => {
        if (room.channel_id === action.payload) {
          room = {
            ...room,
            unread_message_count: 0,
          };
          return room;
        }
        return room;
      });
    },
    updateUnreadMessageCount: (state, action) => {
      let rooms = state.chatRooms.results;

      // When room already exists in the list then update the room info
      if (
        rooms.find(
          (room) => room.channel_id === action.payload.roomInfo.channel_id
        )
      ) {
        rooms = rooms.map((room) => {
          if (room.channel_id === action.payload.roomInfo.channel_id) {
            room = {
              ...room,
              unread_message_count: action.payload.unreadMessageCount,
              last_activity: action.payload.roomInfo.last_activity,
              last_msg_timesince: action.payload.roomInfo.last_msg_timesince,
              last_msg_content: action.payload.roomInfo.last_msg_content,
              last_msg_send_by: action.payload.roomInfo.last_msg_send_by,
            };
            return room;
          }
          return room;
        });
      } else {
        // When room does not exist in the list then add the room to the list
        rooms = [action.payload.roomInfo, ...rooms];
      }

      // sort rooms by last_activity
      rooms.sort((a, b) => {
        return new Date(b.last_activity) - new Date(a.last_activity);
      });

      state.chatRooms.results = rooms;
    },
    fetchChatRoomMessages: (state, action) => {
      state.selectedRoom.messages = action.payload.data;
    },
    loadMoreMessages: (state, action) => {
      state.selectedRoom.messages.pagination = action.payload.data.pagination;
      state.selectedRoom.messages.results = [
        ...state.selectedRoom.messages.results,
        ...action.payload.data.results,
      ];
    },
    deleteMessage: (state, action) => {
      state.selectedRoom.messages.results =
        state.selectedRoom.messages.results.map((message) => {
          if (message.id === action.payload.messageId) {
            return {
              id: message.id,
              send_by: message.send_by,
              message_type: message.message_type,
              created_date_obj: message.created_date_obj,
              sender: message.sender,
              is_deleted: true,
            };
          }
          return message;
        });
    },
    fetchChatFilteredRooms: (state, action) => {
      state.chatFilteredRooms = action.payload;
    },
    blockedUser: (state, action) => {
      // Update selected chat room
      if (action.payload.room_id === state.selectedRoom.channel_id) {
        state.selectedRoom.is_blocked = action.payload.is_blocked;
        state.selectedRoom.blocked_by_list = action.payload.blocked_by_list;
        state.selectedRoom.can_send_message = action.payload.can_send_message;
      }

      // Update chat rooms
      state.chatRooms.results = state.chatRooms.results.map((room) => {
        if (room.channel_id === action.payload.room_id) {
          room = {
            ...room,
            is_blocked: action.payload.is_blocked,
            blocked_by_list: action.payload.blocked_by_list,
            can_send_message: action.payload.can_send_message,
          };
          return room;
        }
        return room;
      });
    },
    unBlockedUser: (state, action) => {
      // Update selected chat room
      if (action.payload.room_id === state.selectedRoom.channel_id) {
        state.selectedRoom.is_blocked = action.payload.is_blocked;
        state.selectedRoom.blocked_by_list = action.payload.blocked_by_list;
        state.selectedRoom.can_send_message = action.payload.can_send_message;
      }

      // Update chat rooms
      state.chatRooms.results = state.chatRooms.results.map((room) => {
        if (room.channel_id === action.payload.room_id) {
          room = {
            ...room,
            is_blocked: action.payload.is_blocked,
            blocked_by_list: action.payload.blocked_by_list,
            can_send_message: action.payload.can_send_message,
          };
          return room;
        }
        return room;
      });
    },
  },
});

export const {
  connectChatSocket,
  disConnectChatSocket,
  fetchChatRooms,
  selectRoom,
  appendNewMessage,
  clearUnreadMessageCount,
  updateUnreadMessageCount,
  fetchChatRoomMessages,
  loadMoreMessages,
  loadMoreChatRooms,
  deleteMessage,
  fetchChatFilteredRooms,
  blockedUser,
  unBlockedUser,
} = chat.actions;
export default chat.reducer;
